import { trackFoodAndBeveragePdpProductInfoSectionExpandableClick } from "@faire/web-api/events/foodAndBeverage/click/pdpProductInfoSectionExpandable";
import { makeObservable } from "@faire/web/common/makeObservable";
import { Button } from "@faire/web/slate/Button";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { action, computed, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";

interface IProps {
  thresholdHeight: number;
}

@observer
export class CollapsibleContainer extends React.Component<
  React.PropsWithChildren<IProps>
> {
  constructor(props: IProps) {
    super(props);
    makeObservable(this);
  }

  @observable
  private elementRef: React.RefObject<HTMLDivElement> = React.createRef();
  @observable
  private isCollapsed: boolean = false;

  @computed
  private get isPassedThreshold() {
    if (!this.elementRef.current) {
      return false;
    }
    const { height } = this.elementRef.current.getBoundingClientRect();
    return height > this.props.thresholdHeight;
  }

  componentDidMount() {
    if (this.isPassedThreshold) {
      this.setIsCollapsed(true);
    }
  }

  @action private setIsCollapsed = (value: boolean) => {
    this.isCollapsed = value;
  };

  private toggleCollapsed = () => {
    const action = this.isCollapsed ? "expand" : "collapse";
    if (isLoggedInRetailer()) {
      trackFoodAndBeveragePdpProductInfoSectionExpandableClick("TAGS", action);
    }
    this.setIsCollapsed(!this.isCollapsed);
  };

  private renderContent = () =>
    this.isCollapsed ? (
      <CollapsedContainer $height={this.props.thresholdHeight}>
        {this.props.children}
      </CollapsedContainer>
    ) : (
      <div>{this.props.children}</div>
    );

  private renderButton = () => {
    if (this.isCollapsed) {
      return (
        <CollapseButton onClick={this.toggleCollapsed}>
          <StrictLocalMsg
            defaultMessage="Show more"
            description={{
              text: "Show more button for product attribute tags on product description page",
              img: "https://cdn.faire.com/fastly/780cf8de2aaeee4818edb6423351f682691e0cbfcaf4b383d4ef67e69ecd42ce.png",
            }}
          />
        </CollapseButton>
      );
    }
    if (this.isPassedThreshold) {
      return (
        <CollapseButton onClick={this.toggleCollapsed}>
          <StrictLocalMsg
            defaultMessage="Show less"
            description={{
              text: "Show less button for product attribute tags on product description page",
              img: "https://cdn.faire.com/fastly/7587c26ccde0e908915475034258bcceade4b36d2a8ef33896a849f56bd65801.png",
            }}
          />
        </CollapseButton>
      );
    }
    return null;
  };

  render() {
    return (
      <div ref={this.elementRef}>
        <Row>{this.renderContent()}</Row>
        {this.renderButton()}
      </div>
    );
  }
}

const CollapsedContainer = styled.div<{ $height: number }>`
  max-height: ${(props) => props.$height}px;
  overflow: hidden;
`;

const CollapseButton = styled(Button).attrs({
  variant: "tertiary",
})`
  margin-top: ${getSpacing("1x")};
`;
